import { useState, useMemo, useEffect } from "react";
import "../../assets/css/Common/DynamicTable.css";
import * as XLSX from "xlsx";
import { FiColumns, FiDownload, FiSearch } from "react-icons/fi";
import ColumnDrawer from "./ColumnDrawer";
import { exportToDesignerExcel } from "../../utils/excelExport";

const extractSearchableStrings = (val, acc = []) => {
  if (val == null) return acc;
  const t = typeof val;
  if (t === "string" || t === "number" || t === "boolean") {
    acc.push(String(val).toLowerCase());
    if (
      t === "string" &&
      (val.includes("-") || val.includes("/")) &&
      val.length >= 8
    ) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        acc.push(`${day}-${month}-${year}`);
        acc.push(`${day}/${month}/${year}`);
      }
    }
    return acc;
  }
  if (Array.isArray(val)) {
    for (let i = 0; i < val.length; i++) {
      extractSearchableStrings(val[i], acc);
    }
    return acc;
  }
  if (t === "object") {
    // Avoid React elements or DOM nodes
    if (val.$$typeof || val._owner || val.nodeType) return acc;
    const keys = Object.keys(val);
    for (let i = 0; i < keys.length; i++) {
      extractSearchableStrings(val[keys[i]], acc);
    }
    return acc;
  }
  return acc;
};

const rowMatchesSearch = (row, query, columns) => {
  if (!row) return false;
  const strings = extractSearchableStrings(row);
  if (columns && Array.isArray(columns)) {
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (col.searchValue) {
        if (typeof col.searchValue === "function") {
          extractSearchableStrings(col.searchValue(row), strings);
        } else if (
          typeof col.searchValue === "string" &&
          row[col.searchValue] !== undefined
        ) {
          extractSearchableStrings(row[col.searchValue], strings);
        }
      } else if (col.exportValue && typeof col.exportValue === "function") {
        extractSearchableStrings(col.exportValue(row), strings);
      } else if (col.render && typeof col.render === "function") {
        try {
          const rendered = col.render(row);
          if (
            typeof rendered === "string" ||
            typeof rendered === "number" ||
            typeof rendered === "boolean"
          ) {
            extractSearchableStrings(rendered, strings);
          }
        } catch {
          // ignore render error during search extraction
        }
      }
    }
  }
  return strings.some((s) => s.includes(query));
};

export default function DynamicTable({
  data = [],
  columns = [],
  onRowClick,
  canDelete = true,
  search = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  filters = [],
  storageKey = "dt_visible_columns",
  selectedIds = [],
  selectedCount = 0,
  onBulkDelete,
  onAdd,
  addButtonText = "Add",
  sortConfig,
  onSortChange,
}) {
  const [columnError, setColumnError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showColumnPanel, setShowColumnPanel] = useState(false);

  const defaultColumns = useMemo(
    () =>
      columns
        .filter((c) => c.id !== "select")
        .map((c) => c.id),
    [columns]
  );

  const [visibleColumns, setVisibleColumns] = useState([]);

  useEffect(() => {
    const saved = sessionStorage.getItem(storageKey);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const validColumns = parsed.filter((id) =>
          defaultColumns.includes(id)
        );

        setVisibleColumns(
          validColumns.length ? validColumns : defaultColumns
        );
      } catch {
        setVisibleColumns(defaultColumns);
      }
    } else {
      setVisibleColumns(defaultColumns);
    }
  }, [defaultColumns, storageKey]);

  useEffect(() => {
    if (visibleColumns.length > 0) {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify(visibleColumns)
      );
    }
  }, [visibleColumns, storageKey]);

  // Reset page when search or pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize]);

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    // Invalid date fallback
    if (isNaN(date.getTime())) return value;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const orderedColumns = useMemo(() => {
    return columns.filter(
      (col) =>
        col.id === "select" ||
        visibleColumns.includes(col.id)
    );
  }, [columns, visibleColumns]);

  // Generic Search Filtering (case-insensitive, trimmed, partial match across row values)
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    if (!search || typeof search !== "string" || !search.trim()) {
      return data;
    }
    const query = search.trim().toLowerCase();
    return data.filter((row) => rowMatchesSearch(row, query, columns));
  }, [data, search, columns]);

  const totalPages = Math.ceil((filteredData?.length || 0) / pageSize);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return (filteredData || []).slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const startRecord =
    !filteredData || filteredData.length === 0
      ? 0
      : (currentPage - 1) * pageSize + 1;

  const endRecord = Math.min(
    currentPage * pageSize,
    filteredData?.length || 0
  );

  const toggleColumn = (id) => {
    if (id === "select") return;

    const isVisible = visibleColumns.includes(id);

    // Guard against hiding the last visible column
    if (isVisible && visibleColumns.length === 1) {
      setColumnError("Please select at least one column.");
      return;
    }

    setColumnError("");

    setVisibleColumns((prev) =>
      isVisible
        ? prev.filter((c) => c !== id)
        : [...prev, id]
    );
  };

  const selectCol = useMemo(
    () => columns.find((c) => c.id === "select"),
    [columns]
  );

  const isRowSelected = (row) => {
    if (!row) return false;
    if (selectedIds && Array.isArray(selectedIds) && selectedIds.length > 0) {
      return selectedIds.some((id) => String(id) === String(row.id));
    }
    if (selectCol && typeof selectCol.render === "function") {
      try {
        const rendered = selectCol.render(row);
        return Boolean(rendered?.props?.checked);
      } catch {
        return false;
      }
    }
    return Boolean(row.isSelected || row._selected);
  };

  const selectedRows = useMemo(() => {
    return (filteredData || []).filter((row) => isRowSelected(row));
  }, [filteredData, selectedIds, selectCol]);

  const rowsToExport = useMemo(() => {
    return selectedRows.length > 0 ? selectedRows : (filteredData || []);
  }, [selectedRows, filteredData]);

  const exportToExcel = async () => {
    const exportCols = orderedColumns.filter((col) => col.id !== "select");
    const count = selectedRows.length;
    const timestamp = Date.now();
    const filename =
      count > 0
        ? `export_selected_${count}_${timestamp}.xlsx`
        : `export_${timestamp}.xlsx`;

    try {
      await exportToDesignerExcel({
        data: rowsToExport,
        columns: exportCols,
        filename,
        sheetName: "CRM Data",
      });
    } catch (err) {
      console.error("Designer excel export failed, falling back to XLSX:", err);
      const exportData = (rowsToExport || []).map((row) => {
        const obj = {};

        exportCols.forEach((col) => {
          let value = col.exportValue
            ? col.exportValue(row)
            : row[col.id];

          if (
            col.id.toLowerCase().includes("date") ||
            col.id.toLowerCase().includes("created") ||
            col.id.toLowerCase().includes("updated") ||
            col.id.toLowerCase().includes("expiry")
          ) {
            value = formatDate(value);
          }

          obj[col.label] = value ?? "-";
        });

        return obj;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Data"
      );

      XLSX.writeFile(
        workbook,
        filename
      );
    }
  };

  return (
    <div className="dt-wrapper">
      <div className="dt-table-wrap">
        {/* Toolbar */}
        <div className="dt-toolbar">
          <div className="dt-toolbar-left">
            <div className="dt-search-wrapper">
              <FiSearch className="dt-search-icon" />
              <input
                className="dt-search"
                placeholder={searchPlaceholder}
                value={search ?? ""}
                onChange={(e) => onSearchChange?.(e.target.value)}
              />
            </div>

            {filters.map((filter) => (
              <select
                key={filter.name}
                id={filter.id || filter.name}
                aria-label={filter.ariaLabel || filter.placeholder}
                data-testid={filter.testId || filter.name}
                className="dt-filter"
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
              >
                <option value="">{filter.placeholder}</option>
                {filter.options.map((option) => {
                  const optVal =
                    typeof option === "object" && option !== null
                      ? option.value
                      : option;
                  const optLabel =
                    typeof option === "object" && option !== null
                      ? option.label
                      : option;
                  return (
                    <option key={optVal} value={optVal}>
                      {optLabel}
                    </option>
                  );
                })}
              </select>
            ))}
          </div>

          <div className="dt-toolbar-right">
            <button
              type="button"
              className="dt-btn"
              onClick={() => setShowColumnPanel(true)}
            >
              <FiColumns />
              <span>Columns</span>
            </button>

            <button
              type="button"
              className="dt-btn"
              onClick={exportToExcel}
              title={
                selectedRows.length > 0
                  ? `Export ${selectedRows.length} selected row${
                      selectedRows.length > 1 ? "s" : ""
                    }`
                  : "Export all visible rows"
              }
            >
              <FiDownload />
              <span>
                {selectedRows.length > 0
                  ? `Export (${selectedRows.length})`
                  : "Export"}
              </span>
            </button>

            {canDelete && selectedCount > 0 && (
              <button
                type="button"
                className="dt-danger"
                onClick={onBulkDelete}
              >
                Delete ({selectedCount})
              </button>
            )}

            {onAdd && (
              <button
                type="button"
                className="dt-add-btn"
                onClick={onAdd}
              >
                + {addButtonText}
              </button>
            )}
          </div>
        </div>

        {/* Column Configuration Drawer */}
        <ColumnDrawer
          open={showColumnPanel}
          onClose={() => setShowColumnPanel(false)}
          columns={columns}
          visibleColumns={visibleColumns}
          toggleColumn={toggleColumn}
          error={columnError}
        />

        {/* Scrollable Table Area */}
        <div className="dt-body-scroll">
          <table className="dt-table">
            <thead>
              <tr>
                {orderedColumns.map((col) => (
                  <th
                    key={col.id}
                    onClick={
                      col.id !== "select"
                        ? () => onSortChange?.(col.id)
                        : undefined
                    }
                    className={`dt-th dt-col-${col.id}`}
                  >
                    <div className="dt-header-content dt-primary">
                      <span>{col.label}</span>

                      {col.id !== "select" && (
                        <span className="dt-sort">
                          {sortConfig?.key === col.id
                            ? sortConfig.direction === "asc"
                              ? "↑"
                              : "↓"
                            : "⇅"}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={orderedColumns.length || 1}
                    className="dt-empty-state"
                  >
                    <div className="dt-empty-content">
                      <span className="dt-empty-text">No results found</span>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedData.map((row) => (
                  <tr
                    key={row.id}
                    className="dt-row"
                    onClick={() => onRowClick?.(row?.id)}
                  >
                    {orderedColumns.map((col, index) => (
                      <td
                        key={col.id}
                        className={`dt-col-${col.id} ${
                          index === 0 ? "dt-primary" : ""
                        }`}
                      >
                        {(() => {
                          const rendered = col.render(row);

                          if (
                            typeof rendered === "string" &&
                            (col.id.toLowerCase().includes("date") ||
                              col.id.toLowerCase().includes("created") ||
                              col.id.toLowerCase().includes("updated") ||
                              col.id.toLowerCase().includes("expiry"))
                          ) {
                            return formatDate(rendered);
                          }

                          return rendered;
                        })()}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="dt-footer">
          <div className="dt-results">
            Showing {startRecord} to {endRecord} of {filteredData?.length || 0} results
          </div>

          <div className="dt-pagination">
            <div className="dt-page-size">
              <span>Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <button
              type="button"
              className="dt-page-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </button>

            <span className="dt-page-info">
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              type="button"
              className="dt-page-btn"
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}